import React, { useState, useEffect, useCallback } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from './Button';
import { GRANTABLE_ROLE_OPTIONS } from '../../data/roleLevels';
import { Pill } from './Pill';
import * as api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

/**
 * ScopeMemberManager — manage the members of one scope (an organization, tenant,
 * or instance) after it has been created. This is the scope-centric twin of
 * MembershipManager (which is user-centric): here the scope is fixed and the
 * operator adds/removes USERS at it, so a scope created without an admin — or
 * one whose admin needs to change — can be managed from its own edit surface.
 *
 * Reuses the existing services verbatim: the scope member readers
 * (getOrgMembers / getTenantMembers / getInstanceMembers), the user search
 * (getUsers), and the membership writers (addUserMembership /
 * removeUserMembership). Org is derived from the tenant server-side, so a
 * tenant/instance scope needs only its own ids. The backend
 * MembershipScopeValidator is the backstop for coherence.
 *
 * Props:
 *   kind        — 'org' | 'tenant' | 'instance' (required)
 *   orgId       — the owning org (required for kind 'org'; optional otherwise, derived server-side)
 *   tenantId    — required for kind 'tenant' | 'instance'
 *   instanceId  — required for kind 'instance'
 *   defaultRole — pre-selected role for a new member (e.g. 'Org Admin')
 *   roleOptions — role strings the operator may grant
 *   canManageRole — optional predicate(role) → boolean
 *   onChanged   — optional callback after any successful mutation
 */
// The grantable subset (everything but the two wildcard tiers) is shared, so a
// new built-in role reaches every member manager at once instead of being
// remembered in each of them. src/data/roleLevels.js
const DEFAULT_ROLE_OPTIONS = GRANTABLE_ROLE_OPTIONS;

function asArray(res) {
  const d = res && (res.data !== undefined ? res.data : res);
  return Array.isArray(d) ? d : [];
}

export default function ScopeMemberManager({
  kind,
  orgId,
  tenantId,
  instanceId,
  defaultRole,
  roleOptions = DEFAULT_ROLE_OPTIONS,
  canManageRole,
  onChanged,
}) {
  const { addToast } = useToast();
  const scopeId = kind === 'instance' ? instanceId : kind === 'tenant' ? tenantId : orgId;
  const scopeNoun = kind === 'instance' ? 'instance' : kind === 'tenant' ? 'tenant' : 'organization';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [role, setRole] = useState(defaultRole || 'Viewer');

  const availableRoles = (roleOptions || DEFAULT_ROLE_OPTIONS)
    .filter((r) => (typeof canManageRole === 'function' ? canManageRole(r) : true));

  const memberFn = kind === 'org' ? api.getOrgMembers
                 : kind === 'tenant' ? api.getTenantMembers
                 : kind === 'instance' ? api.getInstanceMembers
                 : null;

  const reload = useCallback(async () => {
    if (!scopeId || !memberFn) { setRows([]); return; }
    setLoading(true);
    try {
      setRows(asArray(await memberFn(scopeId)));
    } catch (e) {
      addToast({ tone: 'danger', title: 'Could not load members', description: e?.message });
    } finally {
      setLoading(false);
    }
  }, [scopeId, memberFn, addToast]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { setRole(defaultRole || 'Viewer'); }, [defaultRole]);

  // Debounced existing-user search (reuses the same getUsers the create flow uses).
  useEffect(() => {
    if (!showAdd || !query || query.length < 2) { setResults([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const list = asArray(await api.getUsers());
        if (cancelled) return;
        const q = query.toLowerCase();
        setResults(list.filter((u) =>
          (u.email || '').toLowerCase().includes(q) ||
          (`${u.firstName || ''} ${u.lastName || ''}`).toLowerCase().includes(q)
        ).slice(0, 8));
      } catch { if (!cancelled) setResults([]); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [showAdd, query]);

  const afterChange = async () => { await reload(); if (onChanged) onChanged(); };

  const submitAdd = async () => {
    if (!picked) { addToast({ tone: 'danger', title: 'Choose a user to add' }); return; }
    setBusy(true);
    try {
      const res = await api.addUserMembership(picked.id, {
        orgId: orgId || null,
        tenantId: kind === 'org' ? null : (tenantId || null),
        instanceId: kind === 'instance' ? (instanceId || null) : null,
        role,
        isDefault: false,
      });
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Could not add member', description: res.error || res.data?.error });
        return;
      }
      addToast({ tone: 'success', title: 'Member added' });
      setShowAdd(false); setPicked(null); setQuery(''); setResults([]);
      await afterChange();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Could not add member', description: e?.message });
    } finally { setBusy(false); }
  };

  const remove = async (m) => {
    const mid = m.membershipId || m.id; if (!mid || !m.userId) return;
    setBusy(true);
    try {
      const res = await api.removeUserMembership(m.userId, mid);
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Could not remove member', description: res.error || res.data?.error });
        return;
      }
      addToast({ tone: 'success', title: 'Member removed' });
      setConfirmRemove(null);
      await afterChange();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Could not remove member', description: e?.message });
    } finally { setBusy(false); }
  };

  const selectCls = 'v2-focus-ring w-full bg-white text-v2-ink border border-v2-line rounded-v2-md px-3 py-1.5 text-[12.5px] disabled:opacity-50';

  return (
    <div className="space-y-3">
      {loading ? (
        <p className="text-v2-ink-muted text-[12.5px] m-0">Loading members…</p>
      ) : rows.length === 0 ? (
        <p className="text-v2-ink-muted text-[12.5px] m-0">No members yet. Add one below.</p>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((m) => {
            const mid = m.membershipId || m.id;
            const confirming = confirmRemove === mid;
            return (
              <li key={mid || m.userId} className="flex items-center gap-2 text-[12.5px] rounded-v2-md border border-v2-line px-2.5 py-2">
                <span className="min-w-0">
                  <span className="font-semibold text-v2-ink block truncate">{m.name || '(unnamed)'}</span>
                  <span className="text-[10.5px] text-v2-ink-muted block truncate">{m.email}</span>
                </span>
                {m.role && <Pill tone="trust" size="sm">{m.role}</Pill>}
                {m.isDefault && <span className="text-[10px] text-v2-ink-muted italic">default</span>}
                <span className="ml-auto flex items-center gap-1.5">
                  {confirming ? (
                    <>
                      <span className="text-[11px] text-v2-ink-muted">Remove?</span>
                      <Button size="xs" variant="danger" disabled={busy} onClick={() => remove(m)}>Yes</Button>
                      <Button size="xs" variant="ghost" disabled={busy} onClick={() => setConfirmRemove(null)}>No</Button>
                    </>
                  ) : (
                    <Button size="xs" variant="ghost" disabled={busy} onClick={() => setConfirmRemove(mid)} iconLeft={<X size={13} />} aria-label="Remove member" />
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {!showAdd ? (
        <Button size="xs" variant="secondary" iconLeft={<Plus size={14} />} onClick={() => setShowAdd(true)}>
          Add member
        </Button>
      ) : (
        <div className="rounded-v2-md border border-v2-line bg-v2-surface-sub p-3 space-y-2.5">
          {picked ? (
            <div className="flex items-center gap-2 text-[12.5px]">
              <span className="font-semibold text-v2-ink">{picked.name || `${picked.firstName || ''} ${picked.lastName || ''}`.trim() || picked.email}</span>
              <span className="text-[10.5px] text-v2-ink-muted">{picked.email}</span>
              <Button size="xs" variant="ghost" onClick={() => setPicked(null)}>Change</Button>
            </div>
          ) : (
            <div>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search users by name or email…"
                className={selectCls}
                disabled={busy}
              />
              {results.length > 0 && (
                <ul className="mt-1 border border-v2-line rounded-v2-md divide-y divide-v2-line max-h-40 overflow-auto">
                  {results.map((u) => (
                    <li key={u.id}>
                      <button type="button" onClick={() => { setPicked(u); setResults([]); }}
                              className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-v2-cyan-soft">
                        <span className="font-medium text-v2-ink">{`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email}</span>
                        <span className="text-v2-ink-muted ml-2">{u.email}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <div>
            <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">Role</label>
            <select className={selectCls} value={role} disabled={busy} onChange={(e) => setRole(e.target.value)}>
              {availableRoles.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <p className="text-[11px] text-v2-ink-muted m-0">Grants the role on this {scopeNoun}.</p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="primary" disabled={busy || !picked} onClick={submitAdd}>Add member</Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => { setShowAdd(false); setPicked(null); setQuery(''); setResults([]); }}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
